import { useCallback, useMemo, useState } from 'react';

import ArrowForwardIosOutlinedIcon from '@mui/icons-material/ArrowForwardIosOutlined';
import SimCardDownloadOutlinedIcon from '@mui/icons-material/SimCardDownloadOutlined';
import { Box, Divider, FormControlLabel } from '@mui/material';

import Button from '@/components/Buttons/Button';
import Checkbox from '@/components/Checkbox';
import { DataHubCard } from '@/features/DataHub/components/DataHubCard';
import ImageFormatSelection from '@/features/DataHub/modules/Export/components/ImageFormatSelection';
import {
  useExportPreview,
  useExportSettings
} from '@/features/DataHub/modules/Export/context/exportContexts';
import { ExportTypes } from '@/features/DataHub/modules/Export/types';

import { ExportGroup } from './components/Cards';
import { exportOptionsGroups, optionById } from './constants/exportOptions';

const DataExportContent = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { exportSettings, updateExportSetting } = useExportSettings();

  const groupsToShow = isExpanded ? exportOptionsGroups : exportOptionsGroups.slice(0, 1);

  const onToggleIncludeMetadata = useCallback(() => {
    updateExportSetting('includeMetadata', !exportSettings.includeMetadata);
  }, [exportSettings.includeMetadata, updateExportSetting]);

  return (
    <>
      <Divider />
      <FormControlLabel
        control={
          <Checkbox
            checked={exportSettings.includeMetadata}
            sx={theme => ({ fontSize: theme.typography.pxToRem(14) })}
            onChange={onToggleIncludeMetadata}
          />
        }
        label='Include metadata'
        sx={{ m: 0, my: 1 }}
      />
      <Divider sx={{ mb: 2 }} />
      <Box
        sx={{ maxHeight: 400, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        {groupsToShow.map(({ groupName, GroupIcon, options }) => (
          <ExportGroup
            key={groupName}
            GroupIcon={GroupIcon}
            groupName={groupName}
            options={options}
          />
        ))}
      </Box>
      {!isExpanded && (
        <Button
          endIcon={<ArrowForwardIosOutlinedIcon />}
          size='small'
          sx={{
            width: '100%',
            textAlign: 'center',
            mt: 2,
            backgroundColor: 'rgba(248, 248, 248, 0.20)',
            '.MuiButton-endIcon svg': {
              fontSize: '8px !important'
            }
          }}
          variant='contained'
          onClick={() => {
            setIsExpanded(true);
          }}
        >
          Show more options
        </Button>
      )}
    </>
  );
};

const imageFormatLabels: Record<string, string> = {
  'image/png': 'PNG',
  'application/pdf': 'PDF',
  'image/jpeg': 'JPEG',
  'image/svg+xml': 'SVG'
};

const ExportFormat = () => {
  const { exportSettings } = useExportSettings();
  const { previewOptionId } = useExportPreview();
  const isDataExport = exportSettings.exportType === ExportTypes.DATA;

  const collapsedSummary = useMemo(() => {
    let label: string | undefined;
    if (isDataExport) {
      if (!previewOptionId) return undefined;
      label = optionById.get(previewOptionId)?.displayName ?? previewOptionId;
    } else {
      const subType = exportSettings.exportSubType;
      if (!subType) return undefined;
      label = imageFormatLabels[subType] ?? subType;
    }
    if (isDataExport && exportSettings.includeMetadata) {
      return `${label}, Include metadata`;
    }
    return label;
  }, [isDataExport, previewOptionId, exportSettings.exportSubType, exportSettings.includeMetadata]);

  return (
    <DataHubCard
      expandable
      collapsedSummary={collapsedSummary}
      icon={
        <SimCardDownloadOutlinedIcon
          sx={{
            fontSize: 13
          }}
        />
      }
      subtitle='Choose your preferred export method'
      title='Format'
    >
      {isDataExport ? <DataExportContent /> : <ImageFormatSelection />}
    </DataHubCard>
  );
};

export default ExportFormat;
